/* eslint-disable react-hooks/exhaustive-deps */
import type { FC, ReactNode } from 'react'
import { createContext, useState, useContext, useMemo, useEffect } from 'react'

import type { SortOrder } from '@/modules/ecom-policy/types'

type PolicyContext = {
  selectedCategoryIds: [number[] | null, (v: number[] | null) => void]
  searchQuery: [string, (v: string) => void]
  stockFilter: [boolean, (v: boolean) => void]
  sortParam: ['priceFrom' | 'isSelected', (v: 'priceFrom' | 'isSelected') => void]
  sortOrder: [SortOrder, (v: SortOrder) => void]
  onlySelectedFilter: [boolean, (v: boolean) => void]
  onlyMandatoryFilter: [boolean, (v: boolean) => void]
}

type PolicyMetaContext = {
  currentlyBrowsedGroupKey: [string | null, (v: string | null) => void]
  productsWithVisibleAccessoriesIds: number[] | null
  isVatIncluded: [boolean, (v: boolean) => void]
  setProductsWithVisibleAccessoriesIds: (v: number[] | null) => void
}

const initialFiltersContext: PolicyContext = {
  selectedCategoryIds: [null, () => {}],
  searchQuery: ['', () => {}],
  stockFilter: [false, () => {}],
  sortParam: ['isSelected', () => {}],
  sortOrder: ['desc', () => {}],
  onlySelectedFilter: [false, () => {}],
  onlyMandatoryFilter: [false, () => {}],
}

const AccessoryFiltersContext = createContext(initialFiltersContext)
const ProductFiltersContext = createContext(initialFiltersContext)
const PolicyFiltersContext = createContext<PolicyMetaContext>({
  currentlyBrowsedGroupKey: [null, () => {}],
  productsWithVisibleAccessoriesIds: null,
  setProductsWithVisibleAccessoriesIds: () => {},
  isVatIncluded: [false, () => {}],
})

function useFilters() {
  const searchQuery = useState<string>('')
  const stockFilter = useState<boolean>(false)
  const sortParam = useState<'priceFrom' | 'isSelected'>('isSelected')
  const sortOrder = useState<SortOrder>('desc')
  const selectedCategoryIds = useState<number[] | null>(null)
  const onlySelectedFilter = useState(false)
  const onlyMandatoryFilter = useState(false)

  const value: PolicyContext = useMemo(
    () => ({
      searchQuery,
      sortOrder,
      sortParam,
      stockFilter,
      selectedCategoryIds,
      onlySelectedFilter,
      onlyMandatoryFilter,
    }),
    [
      searchQuery[0],
      sortOrder[0],
      sortParam[0],
      stockFilter[0],
      onlySelectedFilter[0],
      selectedCategoryIds[0]?.length,
      onlyMandatoryFilter[0],
    ]
  )

  return value
}

const ProductFiltersContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const filters = useFilters()

  return <ProductFiltersContext.Provider value={filters}>{children}</ProductFiltersContext.Provider>
}

const PolicyFiltersContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const currentlyBrowsedGroupKey = useState<string | null>(null)
  const [productsWithVisibleAccessoriesIds, setProductsWithVisibleAccessoriesIds] = useState<
    number[] | null
  >(null)
  const isVatIncluded = useState(false)

  const value: PolicyMetaContext = useMemo(
    () => ({
      productsWithVisibleAccessoriesIds,
      setProductsWithVisibleAccessoriesIds,
      currentlyBrowsedGroupKey,
      isVatIncluded,
    }),
    [productsWithVisibleAccessoriesIds?.[0], currentlyBrowsedGroupKey[0], isVatIncluded[0]]
  )

  return <PolicyFiltersContext.Provider value={value}>{children}</PolicyFiltersContext.Provider>
}

const AccessoryFiltersContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const filters = useFilters()
  const {
    stockFilter,
    sortParam,
    sortOrder,
    selectedCategoryIds,
    searchQuery,
    onlySelectedFilter,
    onlyMandatoryFilter,
  } = filters
  const { productsWithVisibleAccessoriesIds } = useContext(PolicyFiltersContext)

  // Reset filters on parent product change
  useEffect(() => {
    stockFilter[1](false)
    sortParam[1]('isSelected')
    sortOrder[1]('desc')
    selectedCategoryIds[1](null)
    searchQuery[1]('')
    onlySelectedFilter[1](false)
    onlyMandatoryFilter[1](false)
  }, [productsWithVisibleAccessoriesIds?.[0]])

  return (
    <AccessoryFiltersContext.Provider value={filters}>{children}</AccessoryFiltersContext.Provider>
  )
}

export const PolicyFiltersProvider: FC<{ children: ReactNode }> = ({ children }) => (
  <PolicyFiltersContextProvider>
    <ProductFiltersContextProvider>
      <AccessoryFiltersContextProvider>{children}</AccessoryFiltersContextProvider>
    </ProductFiltersContextProvider>
  </PolicyFiltersContextProvider>
)

export const usePolicyFilters = () => {
  const productFilters = useContext(ProductFiltersContext)
  const accessoryFilters = useContext(AccessoryFiltersContext)
  const policyFilters = useContext(PolicyFiltersContext)

  const {
    productsWithVisibleAccessoriesIds,
    setProductsWithVisibleAccessoriesIds,
    currentlyBrowsedGroupKey,
    isVatIncluded,
  } = policyFilters

  const isAccessory = Boolean(productsWithVisibleAccessoriesIds)

  const filters = isAccessory ? accessoryFilters : productFilters

  return {
    ...filters,
    currentlyBrowsedGroupKey,
    productsWithVisibleAccessoriesIds,
    isVatIncluded,
    setProductsWithVisibleAccessoriesIds,
  }
}
